import React, { useMemo, useState, useRef, useEffect } from 'react';
import useResources from '../hooks/useResources';
import { parseDesc } from '../hooks/useEventos';
import { normISO } from '../utils/surgeryHelpers';

/* ── Configuración ────────────────────────────────────────── */
const HORA_INICIO  = 0;   // 00:00
const HORA_FIN     = 24;  // 24:00 (medianoche)
const SLOT_MIN     = 30;  // minutos por franja
const SLOT_H       = 36;  // px de altura por franja
const HYGIENE_MIN  = 30;  // primera y última media hora = higienización

/* 48 slots: "00:00" … "23:30" */
const SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});

/* ISO → minutos desde medianoche */
const isoToMin = (iso) => {
  const d = new Date(normISO(iso));
  if (isNaN(d)) return null;
  return d.getHours() * 60 + d.getMinutes();
};

/* "HH:MM" → minutos */
const slotToMin = (slot) => {
  const [h, m] = slot.split(':').map(Number);
  return h * 60 + m;
};

/* Formato "mm" → "HH:MM" */
const fmtMin = (m) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/*
  Calcula los tres segmentos de un evento:
  ┌──────────────────────────────────────────────────────┐
  │  higienización entrada  (primeros 30 min)            │  ← gris rayado
  │  procedimiento          (tiempo intermedio)          │  ← color del estado
  │  higienización salida   (últimos 30 min)             │  ← gris rayado
  └──────────────────────────────────────────────────────┘
  Si la duración total ≤ 60 min, los segmentos se comparten proporcionalmente.
*/
const calcSegmentos = (startISO, endISO, fechaISO) => {
  const ini = new Date(normISO(startISO));
  const fin = new Date(normISO(endISO));
  if (isNaN(ini) || isNaN(fin) || fin <= ini) return null;

  // Se dibuja solo la parte del evento que cae en el día mostrado. Así una cirugía
  // de 21:30 a 01:30 aparece el día 2 (21:30–24:00) y el día 3 (00:00–01:30).
  const diaIni = new Date(`${fechaISO}T00:00`);
  const diaFin = new Date(diaIni.getTime() + 24 * 60 * 60000);
  if (isNaN(diaIni) || fin <= diaIni || ini >= diaFin) return null;
  const minDesde = (d) => (d - diaIni) / 60000;

  const inicioMin = Math.max(0, minDesde(ini));
  const finMin    = Math.min(24 * 60, minDesde(fin));
  const durTotal  = (fin - ini) / 60000;        // duración real (puede cruzar medianoche)
  const durDia    = finMin - inicioMin;         // parte visible en este día

  // Higienización: siempre 30 min, pero máximo 1/3 del total si es muy corto
  const hyg = durTotal <= 60 ? Math.floor(durTotal / 3) : HYGIENE_MIN;
  const solape = (a, b) => Math.max(0, Math.min(b, finMin) - Math.max(a, inicioMin));
  const hygTopMin = solape(minDesde(ini), minDesde(ini) + hyg);
  const hygBotMin = solape(minDesde(fin) - hyg, minDesde(fin));
  const procMin   = Math.max(0, durDia - hygTopMin - hygBotMin);

  const px = (min) => (min / SLOT_MIN) * SLOT_H;
  const hm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const otroDia = ini.toDateString() !== fin.toDateString();
  const etiqueta = `${hm(ini)} – ${hm(fin)}${otroDia ? (ini < diaIni ? ' (inició el día anterior)' : ' (+1 día)') : ''}`;

  return {
    top         : px(inicioMin - HORA_INICIO * 60),
    totalHeight : px(durDia),
    hygTop      : { top: 0,                         height: px(hygTopMin) },
    proc        : { top: px(hygTopMin),             height: Math.max(px(procMin), 4) },
    hygBot      : { top: px(hygTopMin + procMin),   height: px(hygBotMin) },
    inicioMin,
    finMin,
    hyg,
    etiqueta,
  };
};

/* ── Colores por estado ───────────────────────────────────── */
const COLOR = {
  pending   : { bg: '#ccf2f0', border: '#04afaa', text: '#027370', proc: '#e6f9f8' },
  confirmada: { bg: '#fde68a', border: '#d97706', text: '#92400e', proc: '#fffbeb' },
  cancelada : { bg: '#fca5a5', border: '#e12f3b', text: '#991b1b', proc: '#fdf1f2' },
  default   : { bg: '#e5e7eb', border: '#9ca3af', text: '#4b5563', proc: '#f9fafb' },
};

/* Patrón SVG de rayas diagonales para higienización */
const hygienePattern = (color) =>
  `repeating-linear-gradient(
    45deg,
    ${color}33 0px,
    ${color}33 4px,
    ${color}11 4px,
    ${color}11 8px
  )`;

/* ── Componente principal ─────────────────────────────────── */
const AgendaView = ({ calendar, eventos, fechaISO, onSlotClick, autoActualizar = false }) => {
  const { salas, loading: loadingSalas } = useResources(calendar);
  const [hoverId, setHoverId] = useState(null);
  const [hoverSlotKey, setHoverSlotKey] = useState(null);
  const scrollRef = useRef(null);

  /* Refresco cada minuto: mueve la línea de "hora actual" y, en el perfil
     Monitor (autoActualizar), vuelve a centrar la vista en la hora actual. */
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  /* Hora actual — debe calcularse antes del useEffect */
  const ahora      = new Date();
  const ahoraMin   = ahora.getHours() * 60 + ahora.getMinutes();
  const hoyLocal   = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
  const esHoy      = fechaISO === hoyLocal;
  const lineaTop   = (ahoraMin / SLOT_MIN) * SLOT_H;

  /* Límite hacia atrás para permitir agendar cirugía "No Programada" */
  const haceVeinticuatroH = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);

  /* Scroll automático al centro de la hora actual cuando es hoy */
  // Para los demás perfiles solo se centra al abrir o cambiar de día (no cada
  // minuto, para no mover la vista mientras el usuario la recorre).
  const anclaScroll = autoActualizar ? lineaTop : `${fechaISO}`;
  useEffect(() => {
    if (!esHoy || !scrollRef.current || loadingSalas) return;
    const containerH = scrollRef.current.clientHeight;
    const scrollTo   = Math.max(0, lineaTop - containerH / 2);
    scrollRef.current.scrollTo({ top: scrollTo, behavior: 'smooth' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esHoy, anclaScroll, loadingSalas]);

  /* Agrupar eventos por sala */
  const eventosPorSala = useMemo(() => {
    const mapa = {};
    eventos.forEach((doc) => {
      const fields = parseDesc(doc?.event?.description);
      const sala   = (fields['AppointmentType'] || '').trim();
      if (!sala) return;
      if (!mapa[sala]) mapa[sala] = [];
      mapa[sala].push(doc);
    });
    return mapa;
  }, [eventos]);

  if (loadingSalas) {
    return (
      <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '12px', padding: '60px 0' }}>
        <div style={{ animation: 'spin .8s linear infinite', border: '3px solid #e5e7eb', borderRadius: '50%', borderTop: '3px solid #04afaa', height: '36px', width: '36px' }} />
        <span style={{ color: '#6b7280', fontSize: '13px' }}>Cargando salas…</span>
      </div>
    );
  }

  if (salas.length === 0) {
    return (
      <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '8px', padding: '60px 0', textAlign: 'center' }}>
        <span style={{ fontSize: '36px' }}>🏥</span>
        <span style={{ color: '#374151', fontSize: '15px', fontWeight: 700 }}>Sin salas configuradas</span>
        <span style={{ color: '#9ca3af', fontSize: '13px' }}>No se encontraron recursos activos para este hospital</span>
      </div>
    );
  }

  const COL_TIME_W = 52;
  const COL_SALA_W = 160;
  const TOTAL_H    = SLOTS.length * SLOT_H; // 48 × 36 = 1728 px

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.08)', overflow: 'hidden' }}>

      {/* ── ENCABEZADO SALAS ─────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '2px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,.06)', display: 'flex', position: 'sticky', top: 0, zIndex: 20 }}>

        {/* Celda esquina */}
        <div style={{ background: '#f9fafb', borderRight: '1px solid #e5e7eb', flexShrink: 0, width: COL_TIME_W }} />

        {/* Columnas de salas */}
        {salas.map((sala) => (
          <div key={sala.id} style={{ borderRight: '1px solid #e5e7eb', flex: `1 0 ${COL_SALA_W}px`, padding: '10px 8px', textAlign: 'center' }}>
            <div style={{ color: '#039490', fontSize: '12px', fontWeight: 800, letterSpacing: '.3px', lineHeight: 1.2, textTransform: 'uppercase' }}>
              {(sala.Resource_Name || '').trim()}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '10px', marginTop: '2px' }}>
              {sala.Resource_Type}
            </div>
          </div>
        ))}
      </div>

      {/* ── GRILLA ───────────────────────────────────────── */}
      <div ref={scrollRef} style={{ display: 'flex', maxHeight: 'calc(100vh - 300px)', overflowX: 'auto', overflowY: 'auto' }}>

        {/* Columna de horas */}
        <div style={{ background: '#f9fafb', borderRight: '1px solid #e5e7eb', flexShrink: 0, width: COL_TIME_W }}>
          {SLOTS.map((slot) => {
            const esHora = slotToMin(slot) % 60 === 0;
            return (
              <div key={slot} style={{
                alignItems    : 'center',
                borderBottom  : `1px solid ${esHora ? '#e5e7eb' : '#f3f4f6'}`,
                color         : esHora ? '#374151' : '#d1d5db',
                display       : 'flex',
                fontSize      : esHora ? '11px' : '9px',
                fontWeight    : esHora ? 700 : 400,
                height        : SLOT_H + 'px',
                justifyContent: 'center',
                letterSpacing : '.3px',
              }}>
                {slot}
              </div>
            );
          })}
        </div>

        {/* Columnas de contenido */}
        <div style={{ display: 'flex', flex: 1, position: 'relative' }}>

          {/* Línea de hora actual */}
          {esHoy && (
            <div style={{ borderTop: '2px solid #e12f3b', left: 0, pointerEvents: 'none', position: 'absolute', right: 0, top: lineaTop + 'px', zIndex: 5 }}>
              <div style={{ background: '#e12f3b', borderRadius: '50%', height: '8px', left: '-4px', position: 'absolute', top: '-4px', width: '8px' }} />
              <span style={{ background: '#e12f3b', borderRadius: '4px', color: '#fff', fontSize: '9px', fontWeight: 700, left: '6px', padding: '1px 5px', position: 'absolute', top: '-9px' }}>
                {fmtMin(ahoraMin)}
              </span>
            </div>
          )}

          {salas.map((sala, salaIndex) => {
            const nombreSala = (sala.Resource_Name || '').trim();
            const evsSala    = eventosPorSala[nombreSala] || [];
            const esUltima   = salaIndex === salas.length - 1;

            // Segmentos ocupados (higienización + procedimiento) de esta sala,
            // para saber qué franjas están realmente disponibles
            // Las cirugías canceladas NO ocupan el espacio: se puede agendar encima.
            const esCancelada = (doc) => String(doc?.event?.summary || '').toLowerCase() === 'cancelada';
            const segsSala = evsSala
              .filter((doc) => !esCancelada(doc))
              .map((doc) => calcSegmentos(doc?.event?.start?.dateTime, doc?.event?.end?.dateTime, fechaISO))
              .filter(Boolean);

            const estaOcupado = (slotMin) =>
              segsSala.some((s) => slotMin >= s.inicioMin && slotMin < s.finMin);

            return (
              <div key={sala.id} style={{ borderRight: '1px solid #e5e7eb', flex: `1 0 ${COL_SALA_W}px`, position: 'relative' }}>

                {/* Franjas de fondo — disponibles son clicables para agendar.
                    Las que ya pasaron pero están dentro de las últimas 24h
                    también son clicables, para registrar una cirugía "No
                    Programada" (con confirmación previa antes de abrir el
                    formulario). */}
                {SLOTS.map((slot) => {
                  const esHora     = slotToMin(slot) % 60 === 0;
                  const slotMin    = slotToMin(slot);
                  const slotDate   = new Date(`${fechaISO}T${slot}`);
                  const esPasado   = slotDate < ahora;
                  const dentroVentana24h = slotDate >= haceVeinticuatroH;
                  const ocupado    = estaOcupado(slotMin);
                  const disponibleFuturo = !esPasado && !ocupado;
                  const disponibleNoProgramada = esPasado && dentroVentana24h && !ocupado;
                  const disponible = onSlotClick && (disponibleFuturo || disponibleNoProgramada);
                  const slotKey    = nombreSala + '|' + slot;
                  const isHoverSlot = hoverSlotKey === slotKey;

                  return (
                    <div
                      key={slot}
                      onClick={disponible ? () => onSlotClick(nombreSala, slot, disponibleNoProgramada) : undefined}
                      onMouseEnter={disponible ? () => setHoverSlotKey(slotKey) : undefined}
                      onMouseLeave={disponible ? () => setHoverSlotKey(null) : undefined}
                      title={
                        disponibleNoProgramada
                          ? `Agendar ${slot} · ${nombreSala} (cirugía no programada)`
                          : disponible ? `Agendar ${slot} · ${nombreSala}` : undefined
                      }
                      style={{
                        borderBottom : `1px solid ${esHora ? '#e5e7eb' : '#f3f4f6'}`,
                        height       : SLOT_H + 'px',
                        background   : disponibleNoProgramada
                          ? (isHoverSlot ? '#fef3c7' : '#fffbeb')
                          : disponibleFuturo
                            ? (isHoverSlot ? '#e6f9f8' : (esHora ? '#fff' : '#fafafa'))
                            : (esHora ? '#fff' : '#fafafa'),
                        cursor       : disponible ? 'pointer' : 'default',
                        transition   : 'background .1s',
                      }}
                    />
                  );
                })}

                {/* Bloques de eventos */}
                {evsSala.map((doc) => {
                  const startISO = doc?.event?.start?.dateTime;
                  const endISO   = doc?.event?.end?.dateTime;
                  const segs     = calcSegmentos(startISO, endISO, fechaISO);
                  if (!segs) return null;

                  const summary  = (doc?.event?.summary || '').toLowerCase();
                  const fields   = parseDesc(doc?.event?.description);
                  const medico   = fields['Medico']        || '';
                  const proc     = fields['Procedimiento'] || '';
                  const paciente = fields['Paciente']      || '';
                  const col      = COLOR[summary] || COLOR.default;
                  const isHover  = hoverId === doc.id;
                  // Cancelada: se muestra tenue y deja pasar el clic al espacio de abajo
                  const cancelada = summary === 'cancelada';

                  return (
                    <div
                      key={doc.id}
                      onMouseEnter={() => setHoverId(doc.id)}
                      onMouseLeave={() => setHoverId(null)}
                      title={cancelada ? 'Cancelada — el espacio está disponible' : undefined}
                      style={{
                        opacity      : cancelada ? 0.45 : 1,
                        pointerEvents: cancelada ? 'none' : 'auto',
                        cursor    : 'pointer',
                        height    : segs.totalHeight - 2 + 'px',
                        left      : '3px',
                        overflow  : 'visible',
                        position  : 'absolute',
                        right     : '3px',
                        top       : segs.top + 1 + 'px',
                        zIndex    : cancelada ? 1 : (isHover ? 10 : 2),
                      }}
                    >
                      {/* Segmento: higienización entrada */}
                      <div style={{
                        background   : hygienePattern(col.border),
                        border       : `1.5px solid ${col.border}`,
                        borderBottom : 'none',
                        borderRadius : '6px 6px 0 0',
                        height       : segs.hygTop.height + 'px',
                        overflow     : 'hidden',
                        padding      : '2px 5px',
                      }}>
                        <span style={{ color: col.text, fontSize: '9px', fontWeight: 700, opacity: .7 }}>
                          🧹 Higienización
                        </span>
                      </div>

                      {/* Segmento: procedimiento */}
                      <div style={{
                        background  : col.proc,
                        border      : `1.5px solid ${col.border}`,
                        borderBottom: 'none',
                        borderTop   : 'none',
                        height      : segs.proc.height + 'px',
                        overflow    : 'hidden',
                        padding     : '3px 6px',
                      }}>
                        {segs.proc.height > 18 && (
                          <div style={{ color: col.text, fontSize: '10px', fontWeight: 800, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {segs.etiqueta}
                          </div>
                        )}
                        {segs.proc.height > 36 && (
                          <div style={{ color: '#374151', fontSize: '10px', fontWeight: 600, lineHeight: 1.3, marginTop: '2px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {proc || medico}
                          </div>
                        )}
                        {segs.proc.height > 58 && medico && (
                          <div style={{ color: '#6b7280', fontSize: '9px', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {medico}
                          </div>
                        )}
                      </div>

                      {/* Segmento: higienización salida */}
                      <div style={{
                        background   : hygienePattern(col.border),
                        border       : `1.5px solid ${col.border}`,
                        borderTop    : 'none',
                        borderRadius : '0 0 6px 6px',
                        height       : segs.hygBot.height + 'px',
                        overflow     : 'hidden',
                        padding      : '2px 5px',
                        display      : 'flex',
                        alignItems   : 'flex-end',
                      }}>
                        <span style={{ color: col.text, fontSize: '9px', fontWeight: 700, opacity: .7 }}>
                          🧹 Higienización
                        </span>
                      </div>

                      {/* Tooltip hover */}
                      {isHover && (
                        <div style={{
                          background   : '#111827',
                          borderRadius : '8px',
                          boxShadow    : '0 4px 16px rgba(0,0,0,.3)',
                          color        : '#fff',
                          fontSize     : '11px',
                          ...(esUltima
                            ? { right: '105%', left: 'auto' }
                            : { left: '105%',  right: 'auto' }),
                          lineHeight   : 1.6,
                          minWidth     : '210px',
                          padding      : '10px 13px',
                          pointerEvents: 'none',
                          position     : 'absolute',
                          top          : '0',
                          whiteSpace   : 'normal',
                          zIndex       : 50,
                        }}>
                          <div style={{ borderBottom: '1px solid rgba(255,255,255,.15)', display: 'flex', gap: '6px', fontWeight: 700, marginBottom: '6px', paddingBottom: '5px', alignItems: 'center' }}>
                            {segs.etiqueta}
                            <span style={{ background: col.border, borderRadius: '4px', fontSize: '9px', padding: '1px 5px', fontWeight: 800 }}>
                              {summary}
                            </span>
                          </div>
                          <div style={{ color: '#d1fae5', fontSize: '9px', marginBottom: '6px' }}>
                            🧹 Higienización: {segs.hyg} min entrada + {segs.hyg} min salida
                          </div>
                          {proc     && <div><span style={{ color: '#9ca3af' }}>Procedimiento: </span>{proc}</div>}
                          {medico   && <div><span style={{ color: '#9ca3af' }}>Médico: </span>{medico}</div>}
                          {paciente && <div><span style={{ color: '#9ca3af' }}>Paciente: </span>{paciente}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── LEYENDA ──────────────────────────────────────── */}
      <div style={{ borderTop: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '10px 16px', alignItems: 'center' }}>
        {[
          { key: 'pending',    label: 'Pendiente'  },
          { key: 'confirmada', label: 'Confirmada' },
          { key: 'cancelada',  label: 'Cancelada'  },
        ].map(({ key, label }) => (
          <div key={key} style={{ alignItems: 'center', display: 'flex', gap: '5px' }}>
            <div style={{ background: COLOR[key].proc, border: `1.5px solid ${COLOR[key].border}`, borderRadius: '3px', height: '12px', width: '18px' }} />
            <span style={{ color: '#6b7280', fontSize: '11px', fontWeight: 500 }}>{label}</span>
          </div>
        ))}
        <div style={{ alignItems: 'center', display: 'flex', gap: '5px' }}>
          <div style={{ background: hygienePattern('#6b7280'), border: '1.5px solid #9ca3af', borderRadius: '3px', height: '12px', width: '18px' }} />
          <span style={{ color: '#6b7280', fontSize: '11px', fontWeight: 500 }}>Higienización (30 min)</span>
        </div>
        {onSlotClick && (
          <div style={{ alignItems: 'center', display: 'flex', gap: '5px' }}>
            <div style={{ background: '#fffbeb', border: '1.5px solid #d97706', borderRadius: '3px', height: '12px', width: '18px' }} />
            <span style={{ color: '#6b7280', fontSize: '11px', fontWeight: 500 }}>Disponible para cirugía no programada (hasta 24h atrás)</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgendaView;
